/**
 * AssessmentStructuredTable — the medications / providers tables inside
 * the Medical Assessment section (prompt v8, care-team request 2026-07:
 * meds + providers render as real tables, not bulleted prose).
 *
 * One generic renderer for both tables (the column set is the only
 * difference), mirroring AdlIadlTable's mechanics and palette: read-only
 * rows when onRowChange is absent; quiet inline text inputs per cell when
 * present. Direct edits win: each keystroke bubbles the patched row up,
 * rides the page's response state into Save, the refine payload, and the
 * .docx. Blanking every cell of a row deletes the row at save (the
 * parser drops all-empty rows), so a wrong row has an inline escape
 * hatch without a model round-trip.
 *
 * Trust state is TABLE-level (one leaf path per table,
 * 'medical_assessment.medications' / '.providers'), not per-row: rows
 * have no stable key (names are editable), so the hairline marks the
 * table wrapper the way EditableProse marks a paragraph.
 */

import { type CSSProperties, type ReactNode } from 'react';
import type { ParagraphState } from './AssessmentDraftOutput';

/** One column: the row field it renders + its header label. */
export interface StructuredTableColumn<Row> {
  field: keyof Row & string;
  label: string;
}

export interface AssessmentStructuredTableProps<Row extends Record<keyof Row, string>> {
  rows: readonly Row[];
  columns: ReadonlyArray<StructuredTableColumn<Row>>;
  /** Present = cells are editable; called with the row index + patch. */
  onRowChange?: (index: number, patch: Partial<Row>) => void;
  /** Table-level trust state ('edited' | 'changed'), hairline on the wrapper. */
  state?: ParagraphState;
  /** The table was viewed (hover/focus) while 'changed'; clears the mark. */
  onSeen?: () => void;
  /** Accessible name + testid stem, e.g. "Current medications". */
  label: string;
  testId: string;
}

const HEAD_STYLE = { color: 'var(--color-sage-800)' } as const;
const BODY_STYLE = { color: 'var(--color-ink)' } as const;

// Quiet inline controls: read as table text until hovered/focused
// (mirrors AdlIadlTable's CONTROL_CLS).
const CONTROL_CLS =
  'w-full rounded-[6px] border border-transparent bg-transparent px-1 py-0.5 text-sm text-ink ' +
  'transition-colors duration-[var(--dur-base)] hover:border-sage-300 hover:bg-white ' +
  'focus-visible:outline-none focus-visible:border-sage-400 focus-visible:ring-1 focus-visible:ring-sage-400';

/** Trust-state hairline on the table wrapper (mirrors EditableProse). */
function wrapperHairline(state: ParagraphState | undefined): CSSProperties {
  return state === 'edited'
    ? { borderLeft: '2px solid var(--color-sage-400)', paddingLeft: '8px' }
    : state === 'changed'
      ? { borderLeft: '2px solid var(--color-warm-400)', paddingLeft: '8px' }
      : {};
}

export function AssessmentStructuredTable<Row extends Record<keyof Row, string>>({
  rows,
  columns,
  onRowChange,
  state,
  onSeen,
  label,
  testId,
}: AssessmentStructuredTableProps<Row>): ReactNode {
  const editable = onRowChange !== undefined;
  const seenHandlers =
    state === 'changed' && onSeen ? { onMouseEnter: onSeen, onFocus: onSeen } : {};

  return (
    <div
      className="overflow-x-auto"
      style={wrapperHairline(state)}
      data-testid={testId}
      data-paragraph-state={state ?? 'drafted'}
      {...seenHandlers}
    >
      <table className="w-full text-sm border-collapse" aria-label={label}>
        <thead>
          <tr className="border-b" style={{ borderColor: 'var(--color-sage-300)' }}>
            {columns.map((c, ci) => (
              <th
                key={c.field}
                className={`text-left font-semibold py-2 ${ci < columns.length - 1 ? 'pr-3' : ''}`}
                style={HEAD_STYLE}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-b"
              style={{ borderColor: 'var(--color-sage-300)' }}
              data-testid={`${testId}-row-${i}`}
            >
              {columns.map((c, ci) => (
                <td
                  key={c.field}
                  className={`py-1.5 align-top ${ci < columns.length - 1 ? 'pr-3' : ''}`}
                  style={BODY_STYLE}
                >
                  {editable ? (
                    <input
                      type="text"
                      className={CONTROL_CLS}
                      value={row[c.field]}
                      onChange={(ev) =>
                        onRowChange(i, { [c.field]: ev.target.value } as Partial<Row>)
                      }
                      aria-label={`${c.label} for ${label} row ${i + 1}`}
                      data-testid={`${testId}-input-${i}-${c.field}`}
                    />
                  ) : (
                    row[c.field]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
